<template>
  <div class="checkout-screen">
    <!-- The cart is only rendered once the order completes, so nothing reserves for it
         before then -- and unlike the other two screens there is no sheet to fall back to
         a 128px guess for. -->
    <div
      class="flex flex-col w-full"
      :class="{ 'pb-32 xl:pb-0 cart-clearance': isCompleted }"
    >
      <CheckoutHeader current-screen="complete" />
      <VPLoadingSpinner :loading="summaryLoading">
        <section
          class="checkout-main"
          :aria-label="$gettext('Order complete')"
        >
          <div class="flex flex-col items-center w-full">
            <div class="text-vp-secondary-1-900 flex gap-4 bg-vp-blue-300 p-2 sm:p-4 my-2 sm:my-4 rounded-sm items-start w-full">
              <template v-if="isCompleted">
                <div class="rotate-45">
                  <iconify-icon
                    icon="mdi:ticket-confirmation-outline"
                  />
                </div>
                <Transition>
                  <div
                    v-if="orderSummary?.customer"
                    class="text-sm"
                  >
                    <h1 class="text-base font-semibold">
                      {{ $gettext('Payment Successful') }}
                    </h1>
                    <p>{{ $gettext("You're all set, %{name}! 🎉", { name: orderSummary.customer.firstName }) }}</p>
                    <p>
                      {{ $gettext('Your tickets are in your inbox at %{email}.', { email: orderSummary.customer.email }) }}
                    </p>
                  </div>
                </Transition>
              </template>
              <template v-else>
                <h1 class="text-base font-semibold">
                  {{ $gettext('The order has not been completed!') }}
                </h1>
              </template>
            </div>

            <template v-if="isCompleted">
              <VPButton
                v-if="tokenValid && !emailForm"
                class="my-4 p-4 rounded-md min-w-full"
                theme="primary"
                variant="outline"
                @click="showEmailChange"
              >
                {{ $gettext('Need to update your email? Click here.') }}
                {{ $gettext('Time remaining: %{time}', { time: formattedRemaining }) }}
              </VPButton>

              <form
                v-if="tokenValid && emailForm"
                class="my-4 flex flex-col items-center w-full"
                @submit.prevent="onUpdateEmail"
              >
                <div class="my-2">
                  {{ $gettext('Time remaining: %{time}', { time: formattedRemaining }) }}
                </div>

                <div class="relative my-2 w-full sm:w-1/2 lg:w-1/3">
                  <input
                    id="email"
                    v-model="newEmail"
                    type="email"
                    :placeholder="$gettext('New email')"
                    autocomplete="email"
                    class="input-floating-label peer"
                  >
                  <label
                    for="email"
                    class="floating-label peer-placeholder-shown:top-1/2 peer-placeholder-shown:transform peer-placeholder-shown:-translate-y-1/2 peer-focus:top-3 peer-focus:text-sm peer-focus:text-vp-base-600 peer-focus:font-normal"
                  >
                    {{ $gettext('New email') }}
                  </label>
                </div>

                <VPButton type="submit">
                  {{ $gettext('Update email') }}
                </VPButton>
              </form>
            </template>
            <!--
            <VPButton
              class="p-4 rounded-md min-w-full"
              theme="primary"
              variant="outline"
              size="xl"
              @click="onNext"
            >{{ $gettext("My tickets") }}
            </VPButton>
          -->
            <VPButton
              component="a"
              :href="homeLink"
              class="p-4 rounded-md min-w-full"
              theme="primary"
              variant="outline"
              size="xl"
            >
              {{ $gettext('Get more tickets') }}
            </VPButton>

            <OrderConfirmationContent
              v-if="isCompleted && orderSummary?.orderConfirmationContent"
              :content="orderSummary.orderConfirmationContent"
            />

            <IceBanner v-if="orderSummary?.event?.showIceBanner" />
          </div>
        </section>
      </VPLoadingSpinner>
    </div>
    <section
      class="cart shopping-cart"
      :aria-label="$gettext('Cart')"
    >
      <VPLoadingSpinner :loading="summaryLoading">
        <ShoppingCart
          v-if="isCompleted && orderSummary?.event && orderSummary.items && orderSummary.breakdown"
          read-only
          :order-summary="orderSummary"
          :event="orderSummary.event"
          :items="orderSummary.items"
          :price-breakdown="orderSummary.breakdown"
          :mobile-next-label="$gettext('Show')"
        />
      </VPLoadingSpinner>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useGettext } from 'vue3-gettext';
import VPButton from './components/VPButton.vue';
import ShoppingCart from './ShoppingCart.vue';
import { useCheckoutStore } from './stores/checkout';
import { VPLoadingSpinner } from '@vp/vue-components';
import { Checkout, Util } from '@vp/common';
import CheckoutHeader from './CheckoutHeader.vue';
import { usePageContext } from 'vike-vue/usePageContext';
import { DateTime, Duration } from 'luxon';
import { useToast } from 'vue-toastification';
import IceBanner from './components/IceBanner.vue';
import OrderConfirmationContent from './components/OrderConfirmationContent.vue';

const { LoadingState } = Util;

type Props = {
  fingerprint: string;
};

const props = defineProps<Props>();

const { $gettext } = useGettext();
const checkoutStore = useCheckoutStore();
const router = useRouter();
const pageContext = usePageContext();
const toast = useToast();

onMounted(() => {
  checkoutStore.loadOrderSummary(props.fingerprint);
  if (tokenExpiresAt.value) {
    updateInterval = setInterval(updateRemaining, 1000);
  }
});

onUnmounted(() => {
  clearInterval(updateInterval);
});

const summaryLoading = computed(() => checkoutStore.orderSummaryLoading != LoadingState.DONE);
const orderSummary = computed<Checkout.OrderSummary.OrderSummary | undefined>(() => checkoutStore.orderSummary);
const changeToken = computed(() => pageContext.changeToken ?? null);
const emailForm = ref(false);
const newEmail = ref('');

const tokenValid = computed(() => !!changeToken.value && changeTimeRemaining.value?.toMillis() > 0);
const tokenExpiresAt = computed(() => changeToken.value?.expiresAt ? DateTime.fromISO(changeToken.value.expiresAt) : undefined);
const homeLink = computed(() => router.resolve({ name: 'OrderNew', params: { eventId: orderSummary.value?.event?.id } }).href);

const changeTimeRemaining = ref<Duration>(Duration.fromMillis(0));
let updateInterval: ReturnType<typeof setInterval> | undefined;

const isCompleted = computed(() => orderSummary.value?.state === 'completed');

const updateRemaining = () => {
  const expiresAt = tokenExpiresAt.value;
  if (!expiresAt) {
    changeTimeRemaining.value = Duration.fromMillis(0);
    clearInterval(updateInterval);
    return;
  }

  changeTimeRemaining.value = DateTime.now().until(expiresAt).toDuration(['minutes', 'seconds']);
  if (!changeTimeRemaining.value.isValid) {
    changeTimeRemaining.value = Duration.fromMillis(0);
    clearInterval(updateInterval);
  }
};

const formattedRemaining = computed(() => changeTimeRemaining.value.toFormat('m:ss'));

const showEmailChange = () => emailForm.value = true;

const onUpdateEmail = async () => {
  const token = changeToken.value?.token;
  if (!token) {
    return;
  }

  try {
    await checkoutStore.updateOrderEmail(props.fingerprint, token, newEmail.value);
    toast.success($gettext('Email updated successfully!'));
    clearInterval(updateInterval);
    pageContext.changeToken = null;
  } catch {
    toast.error($gettext('Failed to update email.'));
  }
};
</script>
